import React, { useEffect, useState } from 'react';
import { useNavigate, Link, Routes, Route } from 'react-router-dom';
import { LogOut, Home, Calendar, Mail, Settings, Users, DollarSign, Image, Rss, Search, BookOpen, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VedLogo from '../../components/VedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const username = localStorage.getItem('admin_username');

  // Earnings tracker state
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().split('T')[0];
  const [earningsStart, setEarningsStart] = useState(firstOfMonth);
  const [earningsEnd, setEarningsEnd] = useState(today);
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // Bottom tab state
  const [activeTab, setActiveTab] = useState('inquiries');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, inquiriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/bookings`, { headers })
      ]);

      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);

      // Auto-sync all iCal feeds in the background on dashboard load
      axios.post(`${BACKEND_URL}/api/admin/ical-feeds/sync-all`, {}, { headers })
        .then(() => { /* silent success */ })
        .catch((err) => { console.debug('iCal auto-sync failed:', err); });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    if (!earningsStart || !earningsEnd) return;
    setEarningsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/earnings?start_date=${earningsStart}&end_date=${earningsEnd}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEarnings(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to fetch earnings data');
      }
    } finally {
      setEarningsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/bookings/${inquiryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Enquiry deleted successfully');
      fetchDashboardData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to delete enquiry');
      }
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${inquiryId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchDashboardData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to update status');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.new;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <VedLogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Ved Homestays Admin</h1>
              <p className="text-sm text-gray-600">Welcome, {username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-red-600 transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="text-blue-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.total_inquiries || 0}</span>
            </div>
            <p className="text-gray-600">Total Inquiries</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Users className="text-yellow-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.new_inquiries || 0}</span>
            </div>
            <p className="text-gray-600">New Inquiries</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="text-green-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.confirmed_bookings || 0}</span>
            </div>
            <p className="text-gray-600">Confirmed</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <DollarSign className="text-orange-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.recent_inquiries || 0}</span>
            </div>
            <p className="text-gray-600">This Week</p>
          </div>
        </div>

        {/* Tabbed section: Booking Inquiries & Earnings Tracker */}
        <div className="bg-white rounded-xl shadow-sm mb-8">
          {/* Tab bar */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-6 py-4 text-sm font-semibold transition-colors focus:outline-none ${
                activeTab === 'inquiries'
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Booking Inquiries
            </button>
            <button
              onClick={() => setActiveTab('earnings')}
              className={`px-6 py-4 text-sm font-semibold transition-colors focus:outline-none flex items-center space-x-2 ${
                activeTab === 'earnings'
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp size={16} />
              <span>Earnings Tracker</span>
            </button>
          </div>

          {/* Booking Inquiries tab */}
          {activeTab === 'inquiries' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Guest</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Dates</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Guests</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Room Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {inquiries.map((inquiry) => (
                      <tr key={inquiry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-800">{inquiry.name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-600">{inquiry.email}</div>
                          <div className="text-sm text-gray-600">{inquiry.phone}</div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {new Date(inquiry.checkIn).toLocaleDateString()} - <br />
                          {new Date(inquiry.checkOut).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{inquiry.guests}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{inquiry.roomType || 'Any'}</td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={inquiry.status}
                            onChange={(e) => updateInquiryStatus(inquiry.id, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <button
                            onClick={() => deleteInquiry(inquiry.id)}
                            className="ml-2 px-3 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded text-sm font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {inquiries.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No booking inquiries yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Earnings Tracker tab */}
          {activeTab === 'earnings' && (
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">Track direct booking earnings for a selected period (based on check-in date).</p>
              <div className="flex flex-wrap items-end gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={earningsStart}
                    onChange={(e) => setEarningsStart(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={earningsEnd}
                    onChange={(e) => setEarningsEnd(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
                <button
                  onClick={fetchEarnings}
                  disabled={earningsLoading}
                  className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 text-sm"
                >
                  {earningsLoading ? 'Loading...' : 'Get Earnings'}
                </button>
              </div>

              {earnings && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{earnings.total_bookings}</p>
                      <p className="text-sm text-blue-600 mt-1">Bookings</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">₹{(earnings.total_received ?? 0).toLocaleString()}</p>
                      <p className="text-sm text-green-600 mt-1">Total Revenue</p>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-orange-700">₹{(earnings.total_advance ?? 0).toLocaleString()}</p>
                      <p className="text-sm text-orange-600 mt-1">Advance Received</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-700">₹{(earnings.total_pending ?? 0).toLocaleString()}</p>
                      <p className="text-sm text-red-600 mt-1">Balance Pending</p>
                    </div>
                  </div>

                  {earnings.bookings.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Booking Ref</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Guest</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Check-In</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Check-Out</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Advance</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Received</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {earnings.bookings.map((b) => (
                            <tr key={b.booking_ref} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-800">{b.booking_ref}</td>
                              <td className="px-4 py-3 text-gray-600">{b.guest_name}</td>
                              <td className="px-4 py-3 text-gray-600">{new Date(b.check_in).toLocaleDateString()}</td>
                              <td className="px-4 py-3 text-gray-600">{new Date(b.check_out).toLocaleDateString()}</td>
                              <td className="px-4 py-3 text-right font-medium text-gray-800">₹{(b.total_amount ?? 0).toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-orange-700 font-medium">₹{(b.advance_received ?? 0).toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-green-700 font-medium">₹{(b.amount_paid ?? 0).toLocaleString()}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  b.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                  b.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                                  b.status === 'checked_out' ? 'bg-gray-100 text-gray-700' :
                                  'bg-red-100 text-red-700'
                                }`}>{b.status.replace('_', ' ')}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No bookings found for the selected period.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/admin/availability"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Search className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Room Availability</h3>
            <p className="text-sm text-gray-600">Check which rooms are available or booked</p>
          </Link>

          <Link
            to="/admin/bookings"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <BookOpen className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Direct Bookings</h3>
            <p className="text-sm text-gray-600">Create bookings, download PDF & send WhatsApp</p>
          </Link>

          <Link
            to="/admin/bookings"
            state={{ tab: 2 }}
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Calendar className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Booking Calendar</h3>
            <p className="text-sm text-gray-600">Monthly calendar view with guest check-in details</p>
          </Link>

          <Link
            to="/admin/pricing"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <DollarSign className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Manage Pricing</h3>
            <p className="text-sm text-gray-600">Set custom prices for specific dates</p>
          </Link>

          <Link
            to="/admin/gallery"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Image className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Manage Gallery</h3>
            <p className="text-sm text-gray-600">Upload and manage photos</p>
          </Link>

          <Link
            to="/admin/ical-sync"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Rss className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">iCal Sync</h3>
            <p className="text-sm text-gray-600">Sync bookings from Airbnb &amp; MakeMyTrip</p>
          </Link>

          <Link
            to="/admin/settings"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Settings className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Settings</h3>
            <p className="text-sm text-gray-600">Manage account and password</p>
          </Link>

          <Link
            to="/"
            target="_blank"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <Home className="text-orange-600 mb-3" size={32} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">View Website</h3>
            <p className="text-sm text-gray-600">See your live website</p>
          </Link>

          <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-sm p-6 text-white">
            <Mail className="mb-3" size={32} />
            <h3 className="text-lg font-bold mb-2">Need Help?</h3>
            <p className="text-sm opacity-90">Contact support</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
